// Renders the permission catalog grouped by category, with a toggle per
// permission. Reused by both the Roles tab (mode='role') and the Accounts tab
// (mode='account').
//
//  - mode='role':    checkbox = whether the role grants the permission.
//  - mode='account': shows the union of role + individual grants with source
//                    badges. A permission inherited from the role is locked
//                    (you can't remove a role grant from one account — that's
//                    the additive-grants model); permissions not granted by the
//                    role have an "Individual" add/remove toggle.
import { PermissionDef } from '../../types/database';

type Mode = 'role' | 'account';

interface PermissionMatrixProps {
  mode: Mode;
  permissions: PermissionDef[];
  /** Permissions granted by the role (the role itself, or the account's role). */
  rolePermissionKeys: Set<string>;
  /** Account mode only: permissions individually granted on the account. */
  individualKeys?: Set<string>;
  /** Role mode: toggle whether the role grants the permission. */
  onToggleRole?: (key: string, enabled: boolean) => void;
  /** Account mode: toggle an individual (additive) grant. */
  onToggleIndividual?: (key: string, enabled: boolean) => void;
  /** A permission key currently being saved (its toggle is disabled). */
  busyKey?: string | null;
  /** Disable all toggles. */
  disabled?: boolean;
}

export default function PermissionMatrix({
  mode,
  permissions,
  rolePermissionKeys,
  individualKeys,
  onToggleRole,
  onToggleIndividual,
  busyKey,
  disabled,
}: PermissionMatrixProps) {
  // Group by category, preserving the catalog's sort order.
  const categories: { name: string; items: PermissionDef[] }[] = [];
  for (const p of permissions) {
    let cat = categories.find((c) => c.name === p.category);
    if (!cat) {
      cat = { name: p.category, items: [] };
      categories.push(cat);
    }
    cat.items.push(p);
  }

  return (
    <div className="perm-matrix">
      {categories.map((cat) => (
        <div className="perm-category" key={cat.name}>
          <h4 className="perm-category-title">{cat.name}</h4>
          {cat.items.map((p) => {
            const fromRole = rolePermissionKeys.has(p.key);
            const fromIndividual = individualKeys?.has(p.key) ?? false;

            if (mode === 'role') {
              const checked = fromRole;
              return (
                <div className="perm-row" key={p.key}>
                  <div className="perm-row-toggle">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled || busyKey === p.key}
                      onChange={(e) => onToggleRole?.(p.key, e.target.checked)}
                    />
                  </div>
                  <div className="perm-row-body">
                    <div className={`perm-row-label ${checked ? 'is-effective' : 'not-effective'}`}>
                      <span className="perm-row-name">{p.label}</span>
                      <span className="perm-row-key">{p.key}</span>
                    </div>
                    {p.description && <div className="perm-row-desc">{p.description}</div>}
                  </div>
                </div>
              );
            }

            // account mode
            const effective = fromRole || fromIndividual;
            // Inherited from the role → locked (can only be removed by editing the
            // role or the account's role). Otherwise the toggle controls the
            // individual grant.
            const locked = fromRole;
            return (
              <div className="perm-row" key={p.key}>
                <div className="perm-row-toggle">
                  <input
                    type="checkbox"
                    checked={effective}
                    disabled={locked || disabled || busyKey === p.key}
                    title={locked ? 'Granted by the role — change it on the Roles tab or change this account’s role' : undefined}
                    onChange={(e) => onToggleIndividual?.(p.key, e.target.checked)}
                  />
                </div>
                <div className="perm-row-body">
                  <div className={`perm-row-label ${effective ? 'is-effective' : 'not-effective'}`}>
                    <span className="perm-row-name">{p.label}</span>
                    {fromRole && <span className="access-badge access-badge--role">Role</span>}
                    {fromIndividual && <span className="access-badge access-badge--individual">Individual</span>}
                    <span className="perm-row-key">{p.key}</span>
                  </div>
                  {p.description && <div className="perm-row-desc">{p.description}</div>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
